import { useEffect } from "react";
import { useRouter } from "@tanstack/react-router";

const MILESTONES = [25, 50, 75, 100] as const;

type ZarazLike = { track: (event: string, props?: Record<string, unknown>) => void };

function getZaraz(): ZarazLike | undefined {
  return (window as unknown as { zaraz?: ZarazLike }).zaraz;
}

function videoTitle(video: HTMLVideoElement): string {
  return video.getAttribute("title") || video.currentSrc;
}

function fireStart(video: HTMLVideoElement) {
  getZaraz()?.track("video_start", {
    video_url: video.currentSrc,
    video_title: videoTitle(video),
    page_path: window.location.pathname,
  });
}

function fireProgress(video: HTMLVideoElement, milestone: number) {
  getZaraz()?.track("video_progress", {
    video_url: video.currentSrc,
    video_title: videoTitle(video),
    video_percent: milestone,
    page_path: window.location.pathname,
  });
}

export function VideoEngagementTracker() {
  const router = useRouter();

  useEffect(() => {
    // Per-video fired milestone sets. WeakMap lets entries be GC'd when a
    // video is removed from the DOM.
    let firedByVideo = new WeakMap<HTMLVideoElement, Set<number>>();
    // Videos that already have listeners attached (never attach twice).
    const attachedVideos = new WeakSet<HTMLVideoElement>();
    // Last time timeupdate logic ran per video (1s throttle).
    let lastCheckByVideo = new WeakMap<HTMLVideoElement, number>();

    function firedSet(video: HTMLVideoElement): Set<number> {
      let set = firedByVideo.get(video);
      if (!set) {
        set = new Set();
        firedByVideo.set(video, set);
      }
      return set;
    }

    function attach(video: HTMLVideoElement) {
      if (attachedVideos.has(video)) return;
      attachedVideos.add(video);
      firedSet(video);

      video.addEventListener(
        "play",
        () => {
          fireStart(video);
        },
        { passive: true },
      );

      video.addEventListener(
        "timeupdate",
        () => {
          const now = Date.now();
          const last = lastCheckByVideo.get(video) ?? 0;
          if (now - last < 1000) return;
          lastCheckByVideo.set(video, now);

          const duration = video.duration;
          if (!duration || !Number.isFinite(duration)) return;
          const percentWatched = Math.floor((video.currentTime / duration) * 100);
          const fired = firedSet(video);
          for (const milestone of MILESTONES) {
            if (percentWatched >= milestone && !fired.has(milestone)) {
              fired.add(milestone);
              fireProgress(video, milestone);
            }
          }
        },
        { passive: true },
      );

      video.addEventListener(
        "ended",
        () => {
          // Safety net: a seek can jump past the 100% timeupdate check.
          const fired = firedSet(video);
          if (!fired.has(100)) {
            fired.add(100);
            fireProgress(video, 100);
          }
        },
        { passive: true },
      );
    }

    function scan(root: ParentNode) {
      if (root instanceof HTMLVideoElement) attach(root);
      root.querySelectorAll?.("video").forEach((v) => attach(v));
    }

    // Attach to any videos already in the DOM.
    scan(document);

    // Watch for dynamically added <video> elements.
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) scan(node);
        });
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    // Reset tracking on route changes: new pages get fresh milestone sets.
    // Listeners stay attached to surviving elements (deduped via WeakSet).
    function reset() {
      // Only milestone state resets. `attachedVideos` MUST persist: the DOM
      // elements (and their listeners) survive route changes, so clearing the
      // dedupe set would attach a second set of listeners and double-count.
      firedByVideo = new WeakMap();
      lastCheckByVideo = new WeakMap();
      scan(document);
    }
    window.addEventListener("popstate", reset);
    const unsubscribe = router.subscribe("onResolved", reset);

    return () => {
      observer.disconnect();
      window.removeEventListener("popstate", reset);
      unsubscribe();
    };
  }, [router]);

  return null;
}
